import React, { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import CreateBookingModal from '@/components/CreateBookingModal';
import PaymentModal from '@/components/PaymentModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { propertyAPI, roomAPI, reservationAPI, paymentAPI, seasonAPI } from '@/services/api';
import { ChevronLeft, ChevronRight, Plus, CalendarPlus, User, Phone, Mail, CreditCard, Calendar, LogIn, LogOut, ChevronDown, ChevronUp, DollarSign, FileText, Trash2 } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = [
  'Habitación Sencilla',
  'Habitación Doble',
  'Habitación Triple',
  'Habitación Cuádruple',
  'Habitación Quíntuple',
  'Suite Junior',
  'Suite Ejecutiva',
  'Suite Premium',
  'Suite Presidencial',
  'Apartamento Estudio',
  'Apartamento 1 Habitación',
  'Apartamento 2 Habitaciones',
  'Apartamento 3 Habitaciones',
  'Penthouse',
  'Cabaña',
  'Villa',
  'Bungalow',
  'Glamping',
  'Otro'
];

const AMENIDADES_LIST = [
  'WiFi', 'TV', 'Aire acondicionado', 'Ventilador', 'Minibar', 'Caja fuerte',
  'Balcón', 'Terraza', 'Vista al mar', 'Vista a la montaña', 'Cocina',
  'Lavadora', 'Secadora', 'Jacuzzi', 'Piscina privada', 'Estacionamiento',
  'Desayuno incluido', 'Baño privado', 'Agua caliente', 'Closet', 'Escritorio'
];

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function CalendarPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedRoomForBooking, setSelectedRoomForBooking] = useState(null);
  const [selectedDateForBooking, setSelectedDateForBooking] = useState(null);
  const [newRoom, setNewRoom] = useState({
    nombre: '',
    tipo: '',
    capacidad: '',
    descripcion: '',
    num_camas: '', tipo_camas: '', num_banos: '',
    num_habitaciones_internas: '', distribucion: '',
    amenidades: [], cantidad: 1
  });
  
  // Reservation detail modal
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [reservationPaymentInfo, setReservationPaymentInfo] = useState({});
  const [seasons, setSeasons] = useState([]);
  const [roomCount, setRoomCount] = useState({ current: 0, limit: 999, remaining: 999 });

  // Drag-to-resize state (shorten/extend reservations by dragging the right edge)
  const CELL_W = 100;
  const ROW_H = 68;
  const [resizing, setResizing] = useState(null); // { reservationId, checkin, initialCheckout, dxDays, minCells }
  const [moving, setMoving] = useState(null); // { reservationId, originalRoomId, startX, startY, deltaY, targetRoomIdx, activated }
  const roomsGridRef = useRef(null);

  // Attach global mouse listeners while moving (drag-to-change-room)
  useEffect(() => {
    if (!moving) return;
    const DRAG_THRESHOLD = 6;
    const onMove = (e) => {
      const dx = e.clientX - moving.startX;
      const dy = e.clientY - moving.startY;
      const activated = moving.activated || Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD;
      let targetRoomIdx = moving.targetRoomIdx;
      if (activated && roomsGridRef.current) {
        const rect = roomsGridRef.current.getBoundingClientRect();
        const relY = e.clientY - rect.top;
        targetRoomIdx = Math.max(0, Math.min(rooms.length - 1, Math.floor(relY / ROW_H)));
      }
      setMoving(m => m ? { ...m, deltaY: dy, deltaX: dx, targetRoomIdx, activated } : null);
    };
    const onUp = async () => {
      const current = moving;
      setMoving(null);
      if (!current || !current.activated) return; // treated as click, not drag
      const targetRoom = rooms[current.targetRoomIdx];
      if (!targetRoom || targetRoom.id === current.originalRoomId) return;
      try {
        const token = localStorage.getItem('token');
        await axios.patch(`${BACKEND_URL}/api/reservations/${current.reservationId}`,
          { room_id: targetRoom.id },
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Reserva movida a ${targetRoom.nombre}`);
        fetchRoomsAndReservations();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'No se pudo cambiar de habitación');
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [moving, rooms]);

  const startMove = (e, res, roomIndex) => {
    // Don't start drag if user clicked on delete or resize handles
    if (e.target.closest('[data-testid^="delete-reservation"]') || e.target.closest('[data-testid^="resize-reservation"]')) return;
    setMoving({
      reservationId: res.id,
      originalRoomId: res.room_id,
      originalRoomIdx: roomIndex,
      startX: e.clientX,
      startY: e.clientY,
      deltaX: 0,
      deltaY: 0,
      targetRoomIdx: roomIndex,
      activated: false,
    });
  };

  // Attach global mouse listeners while resizing
  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const rawDx = e.clientX - resizing.startX;
      const dxDays = Math.round(rawDx / CELL_W);
      const totalNights = Math.max(resizing.minCells, resizing.initialNights + dxDays);
      const boundedDxDays = totalNights - resizing.initialNights;
      setResizing(r => r ? ({ ...r, dxDays: boundedDxDays, previewNights: totalNights }) : null);
    };
    const onUp = async () => {
      const current = resizing;
      setResizing(null);
      if (!current || current.dxDays === 0) return;
      try {
        const token = localStorage.getItem('token');
        const newCheckout = addDays(parseISO(current.checkin), current.previewNights);
        const newCheckoutStr = format(newCheckout, 'yyyy-MM-dd');
        await axios.patch(`${BACKEND_URL}/api/reservations/${current.reservationId}`,
          { fecha_checkout: newCheckoutStr },
          { headers: { Authorization: `Bearer ${token}` } });
        const nights = current.previewNights;
        toast.success(`Reserva ajustada a ${nights} ${nights === 1 ? 'noche' : 'noches'} — precio recalculado`);
        fetchRoomsAndReservations();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'No se pudo ajustar la reserva');
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const startResize = (e, res) => {
    e.stopPropagation();
    e.preventDefault();
    const checkin = res.fecha_checkin;
    const initialNights = differenceInDays(parseISO(res.fecha_checkout), parseISO(checkin));
    setResizing({
      reservationId: res.id,
      checkin,
      initialCheckout: res.fecha_checkout,
      initialNights,
      previewNights: initialNights,
      startX: e.clientX,
      dxDays: 0,
      minCells: 1,
    });
  };

  // Edit Room (from calendar room column)
  const [editRoomOpen, setEditRoomOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editRoomForm, setEditRoomForm] = useState({ nombre: '', tipo: '', capacidad: '' });
  const [savingEditRoom, setSavingEditRoom] = useState(false);

  const openEditRoom = (room) => {
    setEditingRoom(room);
    setEditRoomForm({
      nombre: room.nombre || '',
      tipo: room.tipo || '',
      capacidad: (room.capacidad ?? '').toString(),
    });
    setEditRoomOpen(true);
  };

  const saveEditRoom = async () => {
    if (!editingRoom) return;
    if (!editRoomForm.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSavingEditRoom(true);
    try {
      const token = localStorage.getItem('token');
      const update = {
        nombre: editRoomForm.nombre.trim(),
        tipo: editRoomForm.tipo || editingRoom.tipo,
        capacidad: parseInt(editRoomForm.capacidad) || editingRoom.capacidad,
      };
      await axios.put(`${BACKEND_URL}/api/rooms/${editingRoom.id}`, update, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Habitación actualizada');
      setEditRoomOpen(false);
      setEditingRoom(null);
      fetchRoomsAndReservations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar habitación');
    } finally {
      setSavingEditRoom(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchRoomsAndReservations();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      // Don't override - useGlobalProperty handles initial selection from localStorage
    } catch (error) {
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomsAndReservations = async () => {
    try {
      const token = localStorage.getItem('token');
      const [roomsRes, reservationsRes, seasonsRes, countRes] = await Promise.all([
        roomAPI.getAll(selectedProperty),
        reservationAPI.getAll(selectedProperty),
        seasonAPI.getAll(selectedProperty),
        axios.get(`${BACKEND_URL}/api/rooms/count/${selectedProperty}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { current: 0, limit: 999, remaining: 999 } }))
      ]);
      
      setRooms(roomsRes.data);
      setReservations(reservationsRes.data);
      setSeasons(seasonsRes.data || []);
      setRoomCount(countRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      await roomAPI.create({
        ...newRoom,
        property_id: selectedProperty,
        capacidad: parseInt(newRoom.capacidad),
        num_camas: newRoom.num_camas ? parseInt(newRoom.num_camas) : null,
        num_banos: newRoom.num_banos ? parseInt(newRoom.num_banos) : null,
        num_habitaciones_internas: newRoom.num_habitaciones_internas ? parseInt(newRoom.num_habitaciones_internas) : null,
        cantidad: parseInt(newRoom.cantidad) || 1
      });
      
      toast.success('¡Habitación creada exitosamente!');
      setDialogOpen(false);
      setNewRoom({ nombre: '', tipo: '', capacidad: '', descripcion: '', num_camas: '', tipo_camas: '', num_banos: '', num_habitaciones_internas: '', distribucion: '', amenidades: [], cantidad: 1 });
      fetchRoomsAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear habitación');
    }
  };

  const getDaysInView = () => {
    // Show last 14 days of previous month + full current month + first 14 days of next month.
    // This keeps reservations visible from their actual check-in day, even when they cross
    // a month boundary, with red dividers marking each month change.
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const extendedStart = addDays(start, -14);
    const extendedEnd = addDays(end, 14);
    return eachDayOfInterval({ start: extendedStart, end: extendedEnd });
  };

  const getReservationForRoomAndDate = (roomId, date) => {
    return reservations.find((res) => {
      const checkin = parseISO(res.fecha_checkin);
      const checkout = parseISO(res.fecha_checkout);
      return (
        res.room_id === roomId &&
        date >= checkin &&
        date < checkout
      );
    });
  };

  const previousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const fetchPaymentInfoForReservation = async (reservationId) => {
    try {
      const response = await paymentAPI.getByReservation(reservationId);
      setReservationPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payment info:', error);
      setReservationPaymentInfo({});
    }
  };

  const handleCellClick = (room, date) => {
    // Check if there's already a reservation for this cell
    const existingReservation = getReservationForRoomAndDate(room.id, date);
    if (existingReservation) {
      // Open reservation detail modal
      setSelectedReservation(existingReservation);
      setSelectedRoom(room);
      setReservationModalOpen(true);
      fetchPaymentInfoForReservation(existingReservation.id);
      return;
    }
    
    // Open booking modal for new reservation
    setSelectedRoomForBooking(room);
    setSelectedDateForBooking(date);
    setBookingModalOpen(true);
  };

  const handleCheckIn = async () => {
    if (!selectedReservation) return;
    try {
      await reservationAPI.checkin(selectedReservation.id);
      toast.success('Check-in realizado exitosamente');
      setReservationModalOpen(false);
      fetchRoomsAndReservations();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al realizar check-in');
    }
  };

  const handleCheckOut = async () => {
    if (!selectedReservation) return;
    try {
      await reservationAPI.checkout(selectedReservation.id);
      toast.success('Check-out realizado. Habitación en mantenimiento.');
      setReservationModalOpen(false);
      fetchRoomsAndReservations();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al realizar check-out');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const calendarRef = useRef(null);
  const todayRef = useRef(null);
  const firstOfMonthRef = useRef(null);
  const hasScrolledOnMount = useRef(false);

  // Auto-scroll: prefer scrolling to "today" if visible, otherwise center on day 1 of current month.
  // Uses two RAFs to guarantee the DOM has painted before measuring positions.
  useEffect(() => {
    const doScroll = () => {
      if (!calendarRef.current) return;
      const container = calendarRef.current;
      const target = todayRef.current || firstOfMonthRef.current;
      if (!target) return;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (containerRect.width === 0) return; // not yet laid out
      const scrollLeft = targetRect.left - containerRect.left - containerRect.width / 2 + targetRect.width / 2 + container.scrollLeft;
      // Instant on first mount to avoid jarring smooth-scroll from left edge
      const behavior = hasScrolledOnMount.current ? 'smooth' : 'auto';
      container.scrollTo({ left: Math.max(0, scrollLeft), behavior });
      hasScrolledOnMount.current = true;
    };
    // Wait 2 frames so refs & flex layout finish
    requestAnimationFrame(() => requestAnimationFrame(doScroll));
  }, [rooms, currentDate]);

  const goToToday = () => {
    const now = new Date();
    // If already viewing current month, force a scroll reset by toggling state
    if (isSameDay(startOfMonth(currentDate), startOfMonth(now))) {
      // Trigger effect by re-setting the same date (React sees new object reference)
      setCurrentDate(new Date(now));
    } else {
      setCurrentDate(now);
    }
  };

  const getReservationSpanInView = (reservation, days) => {
    const checkin = parseISO(reservation.fecha_checkin);
    const checkout = parseISO(reservation.fecha_checkout);
    const viewStart = days[0];
    const viewEnd = days[days.length - 1];
    
    const startDay = checkin < viewStart ? viewStart : checkin;
    const endDay = checkout > addDays(viewEnd, 1) ? addDays(viewEnd, 1) : checkout;
    
    const startIdx = days.findIndex(d => isSameDay(d, startDay));
    const daysSpan = differenceInDays(endDay, startDay);
    
    return { startIdx: Math.max(0, startIdx), daysSpan: Math.max(1, daysSpan) };
  };

  const getOccupancyForDay = (day) => {
    if (rooms.length === 0) return 0;
    const occupied = rooms.filter(room => getReservationForRoomAndDate(room.id, day)).length;
    return Math.round((occupied / rooms.length) * 100);
  };

  const getStatusBadge = (estado) => {
    const statusMap = {
      'confirmada': { label: 'Confirmada', class: 'bg-blue-500' },
      'pendiente': { label: 'Pendiente', class: 'bg-yellow-500' },
      'checked_in': { label: 'Check-in', class: 'bg-green-500' },
      'checked_out': { label: 'Check-out', class: 'bg-gray-500' },
      'cancelada': { label: 'Cancelada', class: 'bg-red-500' },
    };
    const status = statusMap[estado] || { label: estado, class: 'bg-gray-400' };
    return <Badge className={`${status.class} text-white`}>{status.label}</Badge>;
  };

  const handleBookingSuccess = () => {
    fetchRoomsAndReservations();
  };

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (e, reservationId, guestName) => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteTarget({ id: reservationId, name: guestName });
  };

  const confirmDeleteReservation = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/reservations/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Reserva de ${deleteTarget.name} eliminada`);
      fetchRoomsAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar reserva');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const days = getDaysInView();

  // Check if a day is within a season
  const getSeasonForDay = (day) => {
    return seasons.find(s => {
      const start = parseISO(s.fecha_inicio);
      const end = parseISO(s.fecha_fin);
      return day >= start && day <= end;
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando calendario...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (rooms.length === 0) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Plus className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-4" style={{fontFamily: 'Manrope'}}>
            No hay habitaciones registradas
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Necesitas agregar habitaciones para ver el calendario. Haz clic en el botón para comenzar.
          </p>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="lg" data-testid="add-room-initial-btn" className="bg-primary hover:bg-primary/90">
                <Plus className="h-5 w-5 mr-2" />
                Agregar Primera Habitación
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{fontFamily: 'Manrope'}}>Nueva Habitación</DialogTitle>
                <DialogDescription>
                  Completa los datos de la habitación o apartamento
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateRoom} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="room_nombre">Nombre / Número *</Label>
                  <Input
                    id="room_nombre"
                    placeholder="Ej: Habitación 101, Apto 2A"
                    value={newRoom.nombre}
                    onChange={(e) => setNewRoom({ ...newRoom, nombre: e.target.value })}
                    required
                    data-testid="room-nombre-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="room_tipo">Tipo de Habitación / Apartamento *</Label>
                  <Select 
                    value={newRoom.tipo} 
                    onValueChange={(value) => setNewRoom({ ...newRoom, tipo: value })}
                  >
                    <SelectTrigger data-testid="room-tipo-select">
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {ROOM_TYPES.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>
                          {tipo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="room_capacidad">Capacidad (personas) *</Label>
                  <Input
                    id="room_capacidad"
                    type="number"
                    min="1"
                    max="20"
                    placeholder="2"
                    value={newRoom.capacidad}
                    onChange={(e) => setNewRoom({ ...newRoom, capacidad: e.target.value })}
                    required
                    data-testid="room-capacidad-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="room_descripcion">Descripción (Opcional)</Label>
                  <Textarea
                    id="room_descripcion"
                    placeholder="Ej: Vista al mar, balcón privado..."
                    value={newRoom.descripcion}
                    onChange={(e) => setNewRoom({ ...newRoom, descripcion: e.target.value })}
                    rows={2}
                    data-testid="room-descripcion-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1"
                    data-testid="room-submit-btn"
                  >
                    Crear Habitación
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="calendar-title">
                Calendario
              </h1>
              {(() => {
                const selProp = properties.find(p => p.id === selectedProperty);
                if (!selProp) return null;
                return (
                  <>
                    <span className="text-lg font-semibold text-muted-foreground" data-testid="calendar-prop-name">{selProp.nombre}</span>
                    {selProp.codigo_unico && (
                      <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs font-mono px-3 py-1" data-testid="calendar-prop-code">{selProp.codigo_unico}</Badge>
                    )}
                  </>
                );
              })()}
            </div>
            <p className="text-muted-foreground mt-1">
              Vista unificada de todas tus reservas
            </p>
          </div>

          <div className="flex items-center gap-2">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[200px]" data-testid="calendar-property-selector">
                  <SelectValue placeholder="Selecciona una propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-room-btn" className="whitespace-nowrap">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{fontFamily: 'Manrope'}}>Nueva Habitación</DialogTitle>
                  <DialogDescription>
                    Completa los datos de la habitación o apartamento
                  </DialogDescription>
                </DialogHeader>
                
                {/* Room limit bar */}
                <div className="bg-slate-50 rounded-lg p-3 border">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-500">Habitaciones creadas</span>
                    <span className="font-bold text-slate-700">{roomCount.current} / {roomCount.limit}</span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${roomCount.current >= roomCount.limit ? 'bg-red-500' : roomCount.current >= roomCount.limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, (roomCount.current / roomCount.limit) * 100)}%` }} />
                  </div>
                  {roomCount.remaining <= 0 && (
                    <p className="text-xs text-red-500 mt-1">Límite alcanzado. Solicita al administrador aumentar el límite.</p>
                  )}
                </div>

                <form onSubmit={handleCreateRoom} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Nombre / Número *</Label>
                      <Input placeholder="Ej: Habitación 101" value={newRoom.nombre} onChange={(e) => setNewRoom({ ...newRoom, nombre: e.target.value })} required data-testid="room-nombre-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Cantidad a crear</Label>
                      <Input type="number" min="1" max="20" value={newRoom.cantidad} onChange={(e) => setNewRoom({ ...newRoom, cantidad: parseInt(e.target.value) || 1 })} data-testid="room-cantidad-input" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Tipo *</Label>
                      <Select value={newRoom.tipo} onValueChange={(v) => setNewRoom({ ...newRoom, tipo: v })}>
                        <SelectTrigger data-testid="room-tipo-select"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {ROOM_TYPES.map((tipo) => (<SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Capacidad (personas) *</Label>
                      <Input type="number" min="1" max="20" placeholder="2" value={newRoom.capacidad} onChange={(e) => setNewRoom({ ...newRoom, capacidad: e.target.value })} required data-testid="room-capacidad-input" />
                    </div>
                  </div>

                  {/* Distribution */}
                  <div className="border rounded-lg p-3 space-y-3 bg-slate-50/50">
                    <Label className="text-xs font-semibold text-slate-600">Distribución</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Camas</Label>
                        <Input type="number" min="0" placeholder="2" value={newRoom.num_camas} onChange={(e) => setNewRoom({ ...newRoom, num_camas: e.target.value })} className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Tipo camas</Label>
                        <Input placeholder="1 doble, 1 sencilla" value={newRoom.tipo_camas} onChange={(e) => setNewRoom({ ...newRoom, tipo_camas: e.target.value })} className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Baños</Label>
                        <Input type="number" min="0" placeholder="1" value={newRoom.num_banos} onChange={(e) => setNewRoom({ ...newRoom, num_banos: e.target.value })} className="h-8 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Habitaciones internas</Label>
                        <Input type="number" min="0" placeholder="0" value={newRoom.num_habitaciones_internas} onChange={(e) => setNewRoom({ ...newRoom, num_habitaciones_internas: e.target.value })} className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Distribución</Label>
                        <Input placeholder="Sala, cocina, 2 hab." value={newRoom.distribucion} onChange={(e) => setNewRoom({ ...newRoom, distribucion: e.target.value })} className="h-8 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Amenities */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-600">Amenidades</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {AMENIDADES_LIST.map(a => {
                        const active = (newRoom.amenidades || []).includes(a);
                        return (
                          <button key={a} type="button" onClick={() => {
                            const list = newRoom.amenidades || [];
                            setNewRoom({ ...newRoom, amenidades: active ? list.filter(x => x !== a) : [...list, a] });
                          }} className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${active ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>
                            {a}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Descripción</Label>
                    <Textarea placeholder="Ej: Vista al mar, balcón privado..." value={newRoom.descripcion} onChange={(e) => setNewRoom({ ...newRoom, descripcion: e.target.value })} rows={2} data-testid="room-descripcion-input" />
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="flex-1" disabled={roomCount.remaining <= 0} data-testid="room-submit-btn">
                      Crear {(parseInt(newRoom.cantidad) || 1) > 1 ? `${newRoom.cantidad} Habitaciones` : 'Habitación'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between bg-slate-800 text-white rounded-xl px-5 py-3 shadow-md">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={previousMonth}
            className="text-white hover:bg-white/10 rounded-full h-9 w-9"
            data-testid="calendar-prev-month-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <h2 className="text-lg font-semibold capitalize tracking-wide" style={{fontFamily: 'Manrope'}}>
            {format(currentDate, 'MMMM yyyy', { locale: es })}
          </h2>

          <Button
            variant="secondary"
            size="sm"
            onClick={goToToday}
            className="bg-white/10 text-white hover:bg-white/20 h-8 px-3 text-xs font-medium rounded-full"
            data-testid="calendar-today-btn"
          >
            Hoy
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={nextMonth}
            className="text-white hover:bg-white/10 rounded-full h-9 w-9"
            data-testid="calendar-next-month-btn"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Calendar Grid - Two-column fixed layout */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex">
            {/* Fixed Left Column - Room Names */}
            <div className="shrink-0 border-r border-slate-200" style={{ width: '200px' }}>
              {/* Header */}
              <div className="bg-slate-800 text-white px-4 py-3 font-semibold text-sm border-b border-slate-700" style={{ height: '80px', display: 'flex', alignItems: 'center' }}>
                Habitación
              </div>
              {/* Room names */}
              {rooms.map((room, roomIndex) => (
                <div
                  key={room.id}
                  onClick={() => openEditRoom(room)}
                  className={`px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-blue-50 transition-colors ${roomIndex % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
                  style={{ height: '68px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  title="Click para editar habitación"
                  data-testid={`calendar-room-edit-${room.id}`}
                >
                  <div className="font-semibold text-sm text-slate-800" style={{fontFamily: 'Manrope'}}>{room.nombre}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{room.tipo}</div>
                </div>
              ))}
            </div>

            {/* Scrollable Right Section - Dates & Cells */}
            <div className="overflow-x-auto" ref={calendarRef} style={{ flex: '1 1 0%', minWidth: 0, maxWidth: 'calc(100vw - 450px)' }}>
              <div style={{ minWidth: `${days.length * 100}px` }}>
                {/* Date Header Row */}
                <div className="flex" style={{ height: '80px' }}>
                  {days.map((day, i) => {
                    const isToday = isSameDay(day, new Date());
                    const occupancy = getOccupancyForDay(day);
                    const season = getSeasonForDay(day);
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const isMonthBoundary = i > 0 && day.getDate() === 1;
                    const isFirstOfCurrentMonth = day.getDate() === 1 && day.getMonth() === currentDate.getMonth() && day.getFullYear() === currentDate.getFullYear();
                    return (
                      <div
                        key={i}
                        ref={isToday ? todayRef : (isFirstOfCurrentMonth ? firstOfMonthRef : null)}
                        className={`shrink-0 border-r border-b border-slate-100 px-1 py-2 text-center flex flex-col justify-center relative ${
                          isToday ? 'bg-amber-50 ring-2 ring-amber-400 ring-inset' : 
                          season ? 'bg-violet-50' :
                          isWeekend ? 'bg-slate-50' : 'bg-white'
                        } ${isMonthBoundary ? 'border-l-2 border-l-red-500' : ''}`}
                        style={{ width: '100px' }}
                        title={season ? `Temporada: ${season.nombre} (x${season.multiplicador})` : (isMonthBoundary ? `Inicio de ${format(day, 'MMMM', { locale: es })}` : '')}
                      >
                        {isMonthBoundary && (
                          <div className="absolute -top-1 left-0 bg-red-500 text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-br z-20 whitespace-nowrap">
                            {format(day, 'MMM', { locale: es })}
                          </div>
                        )}
                        <div className={`text-base font-bold ${isToday ? 'text-amber-600' : season ? 'text-violet-700' : 'text-slate-700'}`}>
                          {format(day, 'd')}
                        </div>
                        <div className={`text-[10px] uppercase font-medium ${isToday ? 'text-amber-500' : 'text-slate-400'}`}>
                          {format(day, 'EEE', { locale: es })}
                        </div>
                        <div className={`text-[9px] font-bold mt-0.5 ${
                          occupancy >= 80 ? 'text-red-500' : occupancy >= 50 ? 'text-amber-500' : 'text-emerald-500'
                        }`}>
                          {occupancy}%
                        </div>
                        {season && (
                          <div className="text-[7px] text-violet-500 font-semibold truncate leading-tight" title={season.nombre}>
                            {season.nombre}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Room Rows with cells */}
                <div ref={roomsGridRef}>
                {rooms.map((room, roomIndex) => {
                  const roomReservations = reservations.filter(r => r.room_id === room.id);
                  const processedReservations = roomReservations.map(res => {
                    const { startIdx, daysSpan } = getReservationSpanInView(res, days);
                    return { ...res, startIdx, daysSpan };
                  }).filter(r => r.startIdx >= 0 && r.startIdx < days.length);

                  const isTargetRoom = moving?.activated && moving.targetRoomIdx === roomIndex && moving.originalRoomId !== room.id;
                  return (
                    <div key={room.id} className={`flex relative ${isTargetRoom ? 'bg-emerald-100 ring-2 ring-emerald-400 ring-inset' : ''}`} style={{ height: '68px' }}>
                      {/* Day Cells */}
                      {days.map((day, i) => {
                        const isToday = isSameDay(day, new Date());
                        const reservation = getReservationForRoomAndDate(room.id, day);
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                        const isMonthBoundary = i > 0 && day.getDate() === 1;
                        return (
                          <div
                            key={i}
                            onClick={() => handleCellClick(room, day)}
                            className={`shrink-0 relative cursor-pointer border-r border-b border-slate-50 transition-all duration-150 ${
                              isToday ? 'bg-amber-50/30' : 
                              isWeekend ? 'bg-slate-50/40' :
                              roomIndex % 2 === 0 ? 'bg-slate-50/20' : ''
                            } ${!reservation ? 'hover:bg-emerald-50/60' : ''} ${isMonthBoundary ? 'border-l-2 border-l-red-500' : ''}`}
                            style={{ width: '100px', height: '68px' }}
                            data-testid={`calendar-cell-${room.id}-${i}`}
                            title={reservation ? `${reservation.guest_name}` : 'Crear reserva'}
                          >
                            {!reservation && (
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                <Plus className="h-4 w-4 text-emerald-300" />
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Reservation Blocks */}
                      {processedReservations.map(res => {
                      const colorMap = {
                        checked_in: 'from-slate-700 to-slate-800',
                        confirmada: 'from-blue-500 to-blue-600',
                        pendiente: 'from-blue-400 to-blue-500',
                        checked_out: 'from-slate-300 to-slate-400',
                        cancelada: 'from-slate-300 to-slate-400'
                      };
                      const isCuentaPorCobrar = res.enviado_a_credito === true;
                      const gradient = isCuentaPorCobrar
                        ? 'from-red-500 to-red-600'
                        : (colorMap[res.estado] || 'from-blue-500 to-blue-600');
                      const leftOffset = res.startIdx * 100;
                      const width = res.daysSpan * 100 - 6;

                      return (
                        <div
                          key={res.id}
                          onMouseDown={(e) => startMove(e, res, roomIndex)}
                          className={`absolute bg-gradient-to-r ${gradient} rounded-lg cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md hover:brightness-105 transition-all z-[5] flex items-center group ${isCuentaPorCobrar ? 'ring-2 ring-red-300' : ''} ${resizing?.reservationId === res.id ? 'ring-2 ring-yellow-400 shadow-lg' : ''} ${moving?.reservationId === res.id && moving?.activated ? 'opacity-50 scale-95 ring-2 ring-blue-400' : ''}`}
                          style={{
                            left: `${leftOffset + 3}px`,
                            width: `${resizing?.reservationId === res.id ? (resizing.previewNights * CELL_W - 6) : width}px`,
                            top: '6px',
                            bottom: '6px',
                            transition: resizing?.reservationId === res.id ? 'none' : 'width 0.15s ease',
                          }}
                          title={isCuentaPorCobrar ? `⚠️ CUENTAS POR COBRAR — ${res.guest_name} | ${res.fecha_checkin} → ${res.fecha_checkout}` : `${res.guest_name} | ${res.fecha_checkin} → ${res.fecha_checkout}`}
                          data-testid={`reservation-block-${res.id}`}
                        >
                          {resizing?.reservationId === res.id && (
                            <div className="absolute -top-8 right-0 bg-yellow-400 text-slate-900 text-xs font-bold px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none z-20">
                              {resizing.previewNights} {resizing.previewNights === 1 ? 'noche' : 'noches'}
                            </div>
                          )}
                          {moving?.reservationId === res.id && moving?.activated && rooms[moving.targetRoomIdx] && rooms[moving.targetRoomIdx].id !== moving.originalRoomId && (
                            <div className="absolute -top-8 left-0 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none z-20">
                              ↳ Mover a: {rooms[moving.targetRoomIdx].nombre}
                            </div>
                          )}
                          {isCuentaPorCobrar && (
                            <div className="absolute -top-2 left-1 bg-red-700 text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shadow-sm z-10 whitespace-nowrap pointer-events-none">
                              Cuentas por cobrar
                            </div>
                          )}
                          <div
                            className="flex-1 flex items-center px-2.5 min-w-0 h-full"
                            onClick={(e) => {
                              // Prevent opening modal if a drag was just completed
                              if (moving?.reservationId === res.id && moving?.activated) {
                                e.stopPropagation();
                                return;
                              }
                              handleCellClick(room, parseISO(res.fecha_checkin));
                            }}
                          >
                            <span className="font-semibold text-[11px] text-white truncate leading-tight">
                              {res.guest_name}
                            </span>
                            {res.daysSpan >= 4 && (
                              <span className="text-[9px] text-white/70 ml-1.5 whitespace-nowrap hidden sm:inline">
                                {format(parseISO(res.fecha_checkin), 'dd/MM')} - {format(parseISO(res.fecha_checkout), 'dd/MM')}
                              </span>
                            )}
                          </div>
                          {/* Drag handle to resize (shorten/extend stay) */}
                          <div
                            onMouseDown={(e) => startResize(e, res)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 top-0 bottom-0 w-2.5 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/30 rounded-r-lg flex items-center justify-center z-[6]"
                            title="Arrastra para acortar o alargar la estadía"
                            data-testid={`resize-reservation-${res.id}`}
                          >
                            <div className="w-0.5 h-4 bg-white/80 rounded-full mr-0.5" />
                            <div className="w-0.5 h-4 bg-white/80 rounded-full" />
                          </div>
                          <button
                            onClick={(e) => handleDeleteClick(e, res.id, res.guest_name)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center h-full pl-1.5 pr-2 rounded-r-lg bg-black/20 hover:bg-red-600/90 mr-2.5"
                            title="Eliminar reserva"
                            data-testid={`delete-reservation-${res.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-white" />
                          </button>
                          <div
                            className="absolute -top-1 -left-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            onClick={(e) => handleDeleteClick(e, res.id, res.guest_name)}
                            title="Eliminar reserva"
                          >
                            <div className="w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg cursor-pointer border-2 border-white">
                              <Trash2 className="h-3 w-3 text-white" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-5">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1">Estados</span>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-slate-700 to-slate-800"></div>
              <span className="text-xs text-slate-600">Check-in</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-blue-500 to-blue-600"></div>
              <span className="text-xs text-slate-600">Pendiente</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-slate-300 to-slate-400"></div>
              <span className="text-xs text-slate-600">Check-out / Cancelada</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-red-500 to-red-600 ring-1 ring-red-300"></div>
              <span className="text-xs text-slate-600">Cuentas por cobrar</span>
            </div>
            <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-400">
              <Trash2 className="h-3 w-3" />
              <span>Hover para eliminar</span>
            </div>
          </div>
        </div>

        {/* Booking Modal */}
        <CreateBookingModal
          open={bookingModalOpen}
          onOpenChange={setBookingModalOpen}
          selectedRoom={selectedRoomForBooking}
          selectedDate={selectedDateForBooking}
          propertyId={selectedProperty}
          onSuccess={handleBookingSuccess}
        />

        {/* Reservation Detail Modal */}
        <Dialog open={reservationModalOpen} onOpenChange={setReservationModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                <Calendar className="h-5 w-5 text-primary" />
                {selectedRoom?.nombre} - {selectedReservation?.guest_name} {selectedReservation?.guest_lastname}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                {selectedReservation && getStatusBadge(selectedReservation.estado)}
                <span className="text-muted-foreground">|</span>
                <span>{selectedRoom?.tipo}</span>
                <span className="text-muted-foreground">|</span>
                <span>{selectedRoom?.capacidad} personas</span>
              </DialogDescription>
            </DialogHeader>

            {selectedReservation && (
              <div className="space-y-4">
                {/* Reservation Summary */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Tarifa</p>
                    <p className="font-bold">{selectedReservation.fuente}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Noches</p>
                    <p className="font-bold">
                      {differenceInDays(
                        parseISO(selectedReservation.fecha_checkout),
                        parseISO(selectedReservation.fecha_checkin)
                      )}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Valor Total</p>
                    <p className="font-bold text-green-600">${selectedReservation.precio_total}</p>
                  </div>
                </div>

                {/* Dates — editable (allows shortening/extending stay even after check-in) */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <Label htmlFor="edit-checkin" className="text-xs text-muted-foreground mb-1">Fecha Ingreso</Label>
                    <Input
                      id="edit-checkin"
                      type="date"
                      value={selectedReservation.fecha_checkin}
                      onChange={async (e) => {
                        const newIn = e.target.value;
                        if (!newIn || newIn >= selectedReservation.fecha_checkout) {
                          toast.error('La fecha de ingreso debe ser anterior a la de salida');
                          return;
                        }
                        try {
                          const token = localStorage.getItem('token');
                          await axios.patch(`${BACKEND_URL}/api/reservations/${selectedReservation.id}`,
                            { fecha_checkin: newIn },
                            { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Fecha de ingreso actualizada — precio recalculado');
                          fetchRoomsAndReservations();
                          setSelectedReservation({ ...selectedReservation, fecha_checkin: newIn });
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Error al actualizar');
                        }
                      }}
                      data-testid="edit-reservation-checkin"
                      className="font-semibold"
                    />
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <Label htmlFor="edit-checkout" className="text-xs text-muted-foreground mb-1">Fecha Salida</Label>
                    <Input
                      id="edit-checkout"
                      type="date"
                      value={selectedReservation.fecha_checkout}
                      min={selectedReservation.fecha_checkin}
                      onChange={async (e) => {
                        const newOut = e.target.value;
                        if (!newOut || newOut <= selectedReservation.fecha_checkin) {
                          toast.error('La fecha de salida debe ser posterior al ingreso');
                          return;
                        }
                        try {
                          const token = localStorage.getItem('token');
                          await axios.patch(`${BACKEND_URL}/api/reservations/${selectedReservation.id}`,
                            { fecha_checkout: newOut },
                            { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Fecha de salida actualizada — precio recalculado');
                          fetchRoomsAndReservations();
                          setSelectedReservation({ ...selectedReservation, fecha_checkout: newOut });
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Error al actualizar');
                        }
                      }}
                      data-testid="edit-reservation-checkout"
                      className="font-semibold"
                    />
                  </div>
                </div>

                {/* Guest Information - Collapsible */}
                <Collapsible open={expandedSections.guest} onOpenChange={() => toggleSection('guest')}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span className="font-medium">Información del Huésped</span>
                    </div>
                    {expandedSections.guest ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 px-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Nombre</p>
                          <p className="font-medium">{selectedReservation.guest_name} {selectedReservation.guest_lastname}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Documento</p>
                          <p className="font-medium">{selectedReservation.guest_documento || 'No registrado'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Teléfono</p>
                          <p className="font-medium">{selectedReservation.guest_phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="font-medium">{selectedReservation.guest_email}</p>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Resumen de Pagos */}
                <div className="p-4 border rounded-lg bg-muted/30" data-testid="calendar-payment-summary">
                  <p className="font-medium text-sm mb-3">Resumen de Pagos</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-white border rounded-lg" data-testid="cal-payment-total">
                      <p className="text-lg font-bold">${(reservationPaymentInfo?.total_a_pagar ?? selectedReservation.precio_total ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-1">Total General</p>
                      {(reservationPaymentInfo?.total_cargos > 0) && (
                        <p className="text-[10px] text-amber-600 mt-0.5">Incluye ${reservationPaymentInfo.total_cargos.toLocaleString()} en consumos</p>
                      )}
                    </div>
                    <div className="text-center p-3 bg-white border rounded-lg" data-testid="cal-payment-abonado">
                      <p className="text-lg font-bold text-green-600">${(reservationPaymentInfo?.total_pagado || 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-1">Valor Abonado</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-500 rounded-lg" data-testid="cal-payment-pendiente">
                      <p className="text-lg font-bold text-white">${(reservationPaymentInfo?.saldo_pendiente ?? selectedReservation.precio_total ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-white/80 mt-1">Saldo Pendiente</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setReservationModalOpen(false)}>
                    Cerrar
                  </Button>
                  <div className="flex-1"></div>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => setPaymentModalOpen(true)}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pagos
                  </Button>
                  {selectedReservation.estado === 'checked_in' ? (
                    <Button 
                      className="bg-red-600 hover:bg-red-700"
                      onClick={handleCheckOut}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Checkout
                    </Button>
                  ) : selectedReservation.estado !== 'checked_out' && selectedReservation.estado !== 'cancelada' && (
                    <Button 
                      className="bg-slate-800 hover:bg-slate-700"
                      onClick={handleCheckIn}
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Checkin
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <PaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          reservation={selectedReservation}
          onPaymentComplete={() => {
            fetchRoomsAndReservations();
            if (selectedReservation) {
              fetchPaymentInfoForReservation(selectedReservation.id);
            }
          }}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar reserva</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de eliminar la reserva de <strong>{deleteTarget?.name}</strong>? Esta acción no se puede deshacer y la habitación quedará disponible nuevamente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">No, cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteReservation}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Eliminando...' : 'Sí, eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Room Dialog (from calendar room column click) */}
        <Dialog open={editRoomOpen} onOpenChange={setEditRoomOpen}>
          <DialogContent className="max-w-md" data-testid="edit-room-modal">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Manrope'}}>Editar Habitación</DialogTitle>
              <DialogDescription>Modifica el nombre, tipo o capacidad de la habitación.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="edit-room-nombre">Nombre *</Label>
                <Input
                  id="edit-room-nombre"
                  value={editRoomForm.nombre}
                  onChange={(e) => setEditRoomForm({ ...editRoomForm, nombre: e.target.value })}
                  placeholder="Ej: Habitación 101"
                  data-testid="edit-room-nombre-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-room-tipo">Tipo</Label>
                  <Select value={editRoomForm.tipo} onValueChange={(v) => setEditRoomForm({ ...editRoomForm, tipo: v })}>
                    <SelectTrigger id="edit-room-tipo" data-testid="edit-room-tipo-select">
                      <SelectValue placeholder="Tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-room-capacidad">Capacidad</Label>
                  <Input
                    id="edit-room-capacidad"
                    type="number"
                    min="1"
                    value={editRoomForm.capacidad}
                    onChange={(e) => setEditRoomForm({ ...editRoomForm, capacidad: e.target.value })}
                    data-testid="edit-room-capacidad-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Para editar amenidades, distribución y más detalles, ve a la sección Habitaciones.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditRoomOpen(false)} disabled={savingEditRoom}>Cancelar</Button>
              <Button onClick={saveEditRoom} disabled={savingEditRoom || !editRoomForm.nombre.trim()} data-testid="edit-room-save-btn">
                {savingEditRoom ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
